/**
 * The Microsoft Graph scopes Omina asks for, in one place.
 *
 * The mirror of `google.ts`, and for the same reason: both the Worker and the
 * browser name these strings, so a typo in one copy produces a grant that
 * looks fine on the consent screen and reads as "not permitted" forever
 * afterwards, with nothing anywhere saying why.
 *
 * **Mail.ReadBasic, not Mail.Read** — and that is a deliberate difference from
 * the Gmail side. `Mail.ReadBasic` grants message metadata and explicitly
 * *withholds* the body, which is exactly the bargain the schema already makes:
 * who, when, which conversation, and never what she wrote. Google has no
 * equivalent — `gmail.readonly` is all-or-nothing — so on that side the
 * promise is kept by our own discipline. Here Microsoft enforces it for us,
 * and it is a smaller thing to ask for on a consent screen.
 *
 * The cost, stated so nobody rediscovers it: fetching a single body live (what
 * `fetchMessageBody` does for Gmail) is impossible under this scope. Nothing
 * in the UI does that today for either provider. If it ever should, this
 * becomes `Mail.Read` and every Outlook founder re-consents — which is why the
 * choice is written down here rather than left as a shrug.
 *
 * Read-only throughout: Omina never writes a calendar and never sends mail.
 */

export const MICROSOFT_CALENDAR_SCOPE = "Calendars.Read";
export const MICROSOFT_MAIL_SCOPE = "Mail.ReadBasic";

/** What "connect my calendar and mail" means, as a request. */
export const MICROSOFT_INTEGRATION_SCOPES: readonly string[] = [
  MICROSOFT_CALENDAR_SCOPE,
  MICROSOFT_MAIL_SCOPE,
];

/**
 * Is this scope in what Microsoft granted?
 *
 * Entra accepts the short name on the way out and hands the long one back:
 * we ask for `Calendars.Read` and the token response says
 * `https://graph.microsoft.com/Calendars.Read`. Comparing the two directly
 * returns false for a perfectly good grant — the same failure AGENTS.md
 * records for Google, arriving from the opposite direction — so both forms
 * are reduced to the last path segment before they are compared.
 *
 * Case-insensitively, because Entra does not preserve the casing it was sent
 * in either.
 */
export function grantsMicrosoftScope(granted: Iterable<string>, wanted: string): boolean {
  const target = shortScope(wanted);
  for (const scope of granted) {
    if (shortScope(scope) === target) return true;
  }
  return false;
}

function shortScope(scope: string): string {
  const trimmed = scope.trim();
  const slash = trimmed.lastIndexOf("/");
  return (slash === -1 ? trimmed : trimmed.slice(slash + 1)).toLowerCase();
}
